import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
} from "@material-ui/core";
import api from "../../../services/api";
import { toast } from "react-toastify";
import DatePicker from "../../UI/DatePicker";
import TimePicker from "../../UI/TimePicker";
import moment from "moment";
import {
  Autocomplete,
  useLoadScript,
} from "@react-google-maps/api";
import CircularLoader from "../../UI/Loader";

const libraries = ["places"];

const CreateRideModal = ({ open, handleClose, user, onRefresh }) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
  });

  const [formData, setFormData] = useState({
    user_id: user?.id || "",
    leaving_from: "",
    heading_to: "",
    departure_date: null,
    departure_time: null,
    total_seats: 4,
    ticket: "",
    vehicle_id: "",
    pickup_location: "",
    drop_location: "",
    duration: "",
    distance: "",
    start_lat: null,
    start_lng: null,
    end_lat: null,
    end_lng: null,
    route: "",
  });
  const [loading, setLoading] = useState(false);
  const [userVehicles, setUserVehicles] = useState([]);

  useEffect(() => {
    if (user?.id) {
      fetchUserVehicles(user.id);
    }
  }, [user]);

  const fetchUserVehicles = async (userId) => {
    try {
      const res = await api.get(`admin/user/${userId}/vehicles`);
      const vehicles = res.data.data || [];
      setUserVehicles(vehicles);
      if (vehicles.length > 0) {
        setFormData(prev => ({ ...prev, vehicle_id: vehicles[0].id }));
      }
    } catch (error) {
      console.error("Failed to fetch vehicles:", error);
    }
  };

  const originRef = useRef();
  const destinationRef = useRef();

  const handleOriginChange = () => {
    const details = originRef.current.getPlace();
    try {
      if (details) {
        const addressComponents = details?.address_components;
        const cityObject = addressComponents.find((component) =>
          component.types.includes("locality")
        );
        const cityData = cityObject ? cityObject.long_name : details.name;

        setFormData((prev) => ({
          ...prev,
          leaving_from: cityData,
          pickup_location: details.formatted_address,
        }));
      }
    } catch (error) {
      console.error("Origin error:", error);
    }
  };

  const handleDestinationChange = async () => {
    const details = destinationRef.current.getPlace();
    try {
      if (details) {
        const addressComponents = details?.address_components;
        const cityObject = addressComponents.find((component) =>
          component.types.includes("locality")
        );
        const cityData = cityObject ? cityObject.long_name : details.name;

        setFormData((prev) => ({
          ...prev,
          heading_to: cityData,
          drop_location: details.formatted_address,
        }));

        await calculateRoute(cityData, details.formatted_address);
      }
    } catch (error) {
      console.error("Destination error:", error);
    }
  };

  const calculateRoute = async (cityData, dropLocation) => {
    if (!originRef.current || !destinationRef.current) return;

    try {
      const originPlace = originRef.current.getPlace();
      const destinationPlace = destinationRef.current.getPlace();

      if (!originPlace || !destinationPlace) return;

      const directionService = new window.google.maps.DirectionsService();
      const results = await directionService.route({
        origin: originPlace.formatted_address,
        destination: destinationPlace.formatted_address,
        travelMode: window.google.maps.TravelMode.DRIVING,
      });

      if (results.routes.length > 0) {
        const leg = results.routes[0].legs[0];
        setFormData((prev) => ({
          ...prev,
          duration: leg.duration.text,
          distance: leg.distance.text,
          start_lat: leg.start_location.lat(),
          start_lng: leg.start_location.lng(),
          end_lat: leg.end_location.lat(),
          end_lng: leg.end_location.lng(),
          route: results.routes[0].summary,
        }));
      }
    } catch (error) {
      console.error("Route calc error:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, departure_date: date });
  };

  const handleTimeChange = (time) => {
    setFormData({ ...formData, departure_time: time });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.departure_date || !formData.departure_time) {
      toast.error("Please select departure date and time");
      return;
    }
    setLoading(true);

    let arrival_date = null;
    let arrival_time = null;

    if (formData.duration) {
      let combinedDateTime = moment(formData.departure_date);
      let t = moment(formData.departure_time);
      combinedDateTime.hours(t.hours());
      combinedDateTime.minutes(t.minutes());

      const regex = /(?:(\d+)\s*hour[s]?)?\s*(\d+)?\s*(min[s]?|minute[s]?)/;
      const match = formData.duration.match(regex);
      if (match) {
        const hours = match[1] ? parseInt(match[1], 10) : 0;
        const minutes = match[2] ? parseInt(match[2], 10) : (match[1] && !hours ? parseInt(match[1], 10) : 0);
        
        combinedDateTime.add(hours, "hours");
        combinedDateTime.add(minutes, "minutes");
        
        arrival_date = combinedDateTime.format("YYYY-MM-DD");
        arrival_time = combinedDateTime.format("h:mm A");
      }
    }

    try {
      const payload = {
        ...formData,
        user_id: user.id,
        arrival_date,
        arrival_time,
        departure_date: moment(formData.departure_date).format("YYYY-MM-DD"),
        departure_time: moment(formData.departure_time).toISOString(),
      };
      const res = await api.post("admin/ride/create", payload);
      if (res.status === 200) {
        toast.success("Ride created successfully");
        if (onRefresh) onRefresh();
        handleClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create ride");
    } finally {
      setLoading(false);
    }
  };

  if (!isLoaded) return <CircularLoader isLoading={!isLoaded} />;

  return (
    <>
      <style>{`
        .pac-container {
          z-index: 10000 !important;
        }
      `}</style>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableEnforceFocus>
        <DialogTitle>Create Ride for {user?.first_name} {user?.last_name}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Leaving From
                </Typography>
                <Autocomplete
                  options={{ componentRestrictions: { country: "pk" } }}
                  onLoad={(autocomplete) => {
                    originRef.current = autocomplete;
                  }}
                  onPlaceChanged={handleOriginChange}
                >
                  <input
                    placeholder="Street address or city"
                    style={{
                      width: "100%",
                      padding: "12.5px 14px",
                      fontSize: "1rem",
                      border: "1px solid #c4c4c4",
                      borderRadius: "4px",
                      marginTop: "4px",
                    }}
                    required
                  />
                </Autocomplete>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Heading To
                </Typography>
                <Autocomplete
                  options={{ componentRestrictions: { country: "pk" } }}
                  onLoad={(autocomplete) => {
                    destinationRef.current = autocomplete;
                  }}
                  onPlaceChanged={handleDestinationChange}
                >
                  <input
                    placeholder="Street address or city"
                    style={{
                      width: "100%",
                      padding: "12.5px 14px",
                      fontSize: "1rem",
                      border: "1px solid #c4c4c4",
                      borderRadius: "4px",
                      marginTop: "4px",
                    }}
                    required
                  />
                </Autocomplete>
              </Grid>

              {formData.distance && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="primary">
                    Distance: {formData.distance} | Duration: {formData.duration}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={6}>
                <DatePicker
                  label="Departure Date"
                  date={formData.departure_date}
                  handleDateChange={handleDateChange}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TimePicker
                  label="Departure Time"
                  time={formData.departure_time}
                  handleTimeChange={handleTimeChange}
                  date={formData.departure_date}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Ticket Price"
                  name="ticket"
                  type="number"
                  fullWidth
                  required
                  variant="outlined"
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Total Seats"
                  name="total_seats"
                  type="number"
                  fullWidth
                  required
                  variant="outlined"
                  value={formData.total_seats}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                select
                label="Select Vehicle"
                name="vehicle_id"
                fullWidth
                variant="outlined"
                value={formData.vehicle_id || ""}
                onChange={handleChange}
                required
              >
                {userVehicles.length === 0 ? (
                  <MenuItem value="" disabled>No vehicles available</MenuItem>
                ) : (
                  userVehicles.map(v => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.vehicle_name} {v.vehicle_model ? `(${v.vehicle_model})` : ''} - {v.registration_no || 'No Reg'}
                    </MenuItem>
                  ))
                )}
              </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Pickup Address (Calculated)"
                  value={formData.pickup_location}
                  fullWidth
                  variant="outlined"
                  disabled
                  placeholder="Select origin above"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Drop Address (Calculated)"
                  value={formData.drop_location}
                  fullWidth
                  variant="outlined"
                  disabled
                  placeholder="Select destination above"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary">
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={loading || !formData.distance}
            >
              {loading ? "Creating..." : "Create Ride"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default CreateRideModal;
